// Iter 91k — Global rejection email editor for Settings → Platform.
//
// Super admins polish the company-wide copy that goes out when a
// candidate is moved to "Rejected" on any posting that doesn't have
// its own per-posting override (`/api/jobs/{posting_id}/rejection-template`).
//
// Backend contract:
//   GET   /api/jobs/settings                       → { sandbox, rejection_email_template, ... }
//   PATCH /api/jobs/settings { rejection_email_template } → returns fresh settings
//
// Tokens auto-swap at send: {{name}}, {{email}}, {{title}}, {{department}}.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, MailCheck, Info, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const TOKENS = ["name", "email", "title", "department"];

// Kept in sync with backend `_render_template` and the per-posting
// dialog so the live preview matches the outbound email.
function renderLocal(tpl, ctx) {
  let out = tpl || "";
  Object.entries(ctx || {}).forEach(([k, v]) => {
    out = out.replace(new RegExp(`{{\\s*${k}\\s*}}`, "g"), String(v || ""));
  });
  return out;
}

const SAMPLE_CTX = {
  name:       "Alex Candidate",
  email:      "candidate@example.com",
  title:      "the role",
  department: "your department",
};

export function JobBoardRejectionCopyEditor() {
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [draft,    setDraft]    = useState("");
  const [saving,   setSaving]   = useState(false);
  const [open,     setOpen]     = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    api.get("/jobs/settings").then((r) => {
      setSettings(r.data);
      setDraft(r.data?.rejection_email_template || "");
    }).catch(() => setSettings(null));
  }, []);

  const preview = useMemo(
    () => renderLocal(draft || (settings?.rejection_email_template || ""), SAMPLE_CTX),
    [draft, settings],
  );

  const dirty = settings && draft !== (settings.rejection_email_template || "");

  if (!settings) return null;

  const save = async () => {
    try {
      setSaving(true);
      const { data } = await api.patch("/jobs/settings",
        { rejection_email_template: (draft || "").trim() });
      setSettings(data);
      setDraft(data.rejection_email_template || "");
      toast.success("Global rejection copy saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200"
         data-testid="jobs-rejection-copy-editor">
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid="jobs-rejection-copy-toggle"
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50/60 rounded-lg">
        <div className="flex items-start gap-2">
          <MailCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
              Rejection email copy
              <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-600">Global</span>
            </div>
            <div className="text-xs text-slate-400">
              Fallback copy used when a posting has no per-posting override. Recruiters override per posting on the Job Board.
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" />
              : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-3">
          {/* Sandbox / status banner */}
          <div className={`rounded-md border p-2.5 text-xs flex items-start gap-2 ${
            settings.sandbox
              ? "border-amber-200 bg-amber-50 text-amber-800"
              : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}
               data-testid="jobs-rejection-copy-sandbox-banner">
            {settings.sandbox
              ? <ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              : <MailCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />}
            <div>
              {settings.sandbox
                ? "Job Board is in sandbox — this copy is audit-logged only. Flip the sandbox switch above to send real emails."
                : "Job Board is LIVE — this copy will send to real candidates when they're moved to Rejected."}
            </div>
          </div>

          {/* Token help */}
          <div className="rounded-md border border-indigo-100 bg-indigo-50/60 p-2.5 text-xs text-indigo-900 flex items-start gap-2">
            <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>
              Tokens auto-swap on send:&nbsp;
              {TOKENS.map((t) => (
                <code key={t}
                      className="mx-0.5 rounded bg-white px-1 py-0.5 font-mono text-[10px] text-indigo-800 border border-indigo-100">
                  {`{{${t}}}`}
                </code>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Copy</Label>
            <Textarea rows={11}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      disabled={!isSuper || saving}
                      data-testid="jobs-rejection-copy-textarea"
                      className="font-mono text-xs" />
            {!isSuper && (
              <div className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                <ShieldAlert className="h-3 w-3" /> Super admin only — read-only for other roles
              </div>
            )}
          </div>

          {/* Preview */}
          <div className="space-y-1.5">
            <Label>Preview</Label>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800 whitespace-pre-wrap"
                 data-testid="jobs-rejection-copy-preview">
              <div className="mb-1 text-[10px] font-bold uppercase text-slate-500">
                Subject: Your application for {SAMPLE_CTX.title}
              </div>
              {preview || (
                <span className="italic text-slate-400">
                  Preview will show once copy is added.
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-400">
              Preview uses sample candidate <b>{SAMPLE_CTX.name}</b>. The real
              send pulls the actual candidate name/email off the resume record
              and posting title.
            </div>
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" disabled={saving || !dirty}
                    onClick={() => setDraft(settings.rejection_email_template || "")}
                    data-testid="jobs-rejection-copy-revert">
              Revert
            </Button>
            <Button size="sm" onClick={save} disabled={saving || !dirty || !isSuper}
                    data-testid="jobs-rejection-copy-save" className="gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save global copy
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
